import { Link } from 'react-router-dom';

/**
 * Upper band per PRD §5.1 (revised layout).
 *
 * Two-column banner on desktop: left half carries the platform
 * headline + subtitle + primary CTAs; right half carries the
 * PRD §5.1 hero diagram (org chart transforming into a neural
 * network) at a contained size. On mobile the two stack, with
 * the diagram below the text so nothing crops on narrow viewports.
 *
 * Headline mirrors the /what-is-dideck hero so the home banner and
 * the What-is-DI-Deck page share one consistent brand statement.
 */
export function Banner(): JSX.Element {
  return (
    <section
      aria-label="DI Deck — a sovereign Decision Orchestration Platform"
      className="relative flex min-h-[42vh] flex-col overflow-hidden bg-gradient-to-br from-brand-purple-dark via-brand-purple to-brand-purple-light text-white"
    >
      {/* Top chrome: brand mark, left-aligned */}
      <header className="relative flex items-center px-6 py-4 md:px-10">
        <div className="flex items-center gap-3">
          <img src="/di-icon.png" alt="" aria-hidden className="h-8 w-8" />
          <span className="text-lg font-extrabold tracking-tight">DI DECK</span>
        </div>
      </header>

      {/* Full-width title spanning the whole banner */}
      <div className="relative px-6 pt-2 md:px-10">
        <h1 className="text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
          A sovereign Decision Orchestration Platform
        </h1>
      </div>

      {/* Two-column content below the title — subtitle + CTAs left, diagram right */}
      <div className="relative grid flex-1 items-center gap-6 px-6 pb-6 pt-4 md:grid-cols-2 md:gap-10 md:px-10 md:pb-10">
        <div className="max-w-xl">
          <p className="max-w-lg text-base text-white/85 md:text-lg">
            Every manager a neural unit. Every decision anchored. Nothing leaves your boundary.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/what-is-dideck"
              className="rounded-lg bg-white px-5 py-3 text-sm font-bold text-brand-purple shadow-card transition-all hover:shadow-card-hover"
            >
              What is DI Deck →
            </Link>
            <Link
              to="/pricing"
              className="rounded-lg border border-white/60 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              See pricing
            </Link>
          </div>
        </div>

        {/* Hero diagram — the org-chart-to-neural-network illustration.
            The image PNG has its dark-purple background pre-stripped to
            transparent so the banner\'s own gradient shows through — no
            visible frame or edge, the diagram merges into the banner. */}
        <div className="flex min-h-[240px] w-full items-center justify-center py-2 md:h-full md:min-h-[340px] md:py-3">
          <img
            src="/hero-neural.png?v=2"
            alt="An enterprise org chart transforming into a neural network — every manager a neural unit"
            className="max-h-full w-auto max-w-full md:-translate-x-16 lg:-translate-x-28 xl:-translate-x-40"
          />
        </div>
      </div>
    </section>
  );
}

